import { useEffect, useState } from "react";
import { Box, Divider, Rating } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  TextInput,
  RadioButtonGroupInput,
  BooleanInput,
  TabbedForm,
  NumberInput,
  FormTab,
  CheckboxGroupInput,
  ArrayInput,
  SimpleFormIterator,
  ImageInput,
  ImageField,
  FormDataConsumer,
  Labeled,
  useRecordContext,
  FunctionField,
  SelectInput,
  useInput,
} from "react-admin";

import { DateInput } from "../../components/_DateInput";
import OperationRadiusInput from "../../components/operation-radius-input";
import {
  serviceNameList,
  enrichmentActivitiesList,
  referenceStatusList,
} from "../../utils";
import EditRatingsReviews from "./EditRatingsReviews";
import EditOtherRatingsReviews from "./EditOtherRatingsReviews";

const styles = {
  twoColLeft: { display: "inline-block" },
  twoColRight: { display: "inline-block", marginLeft: 32 },
};

const useStyles = makeStyles(styles);

const dateToTimestamp = (date) => {
  return +new Date(date);
};

const uploadFileToS3 = async (file, callback) => {
  const { path, name, size, type } = file;
  if (!(path && name && size && type)) {
    return;
  }

  const initiateResponse = await fetch(
    `${process.env.REACT_APP_API_URL}user/123/photos/initiate-upload?contentType=${file.type}`,
    {
      method: "post",
    },
  );
  const { writeUrl, publicUrl } = await initiateResponse.json();

  await fetch(writeUrl, {
    method: "put",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (callback) {
    callback(publicUrl);
  }
};

const RatingInput = (props) => {
  const { field } = useInput(props);
  const [ratingValue, setRatingValue] = useState(field.value);

  return (
    <>
      <Rating
        label="Rating"
        value={parseFloat(ratingValue) || 0}
        onChange={(event) => {
          setRatingValue(event.target.value);
          field.onChange(event);
        }}
        precision={0.5}
      />
    </>
  );
};

const UserForm = ({ profilePic, setProfilePic, isEdit, ...props }) => {
  const classes = useStyles();
  const record = useRecordContext();

  useEffect(() => {
    setProfilePic(record.profilePic);
  }, [record?.profilePic, setProfilePic]);

  return (
    <TabbedForm {...props}>
      <FormTab label="Basics">
        <ImageInput
          source="picture"
          label="Photo"
          accept="image/*"
          onChange={(file) => uploadFileToS3(file, setProfilePic)}
        >
          <Box sx={{ width: 150, "& img": { width: "100%" } }}>
            <img src={profilePic} title="Avatar" alt="profile avatar" />
          </Box>
        </ImageInput>

        <FormDataConsumer>
          {({ formData }) =>
            !formData.src && (
              <div>
                <Labeled label="Original image">
                  <ImageField
                    source="profilePic"
                    sx={{
                      "& .RaImageField-image": {
                        width: 180,
                        height: "auto",
                        objectFit: "contain",
                      },
                    }}
                  />
                </Labeled>
              </div>
            )
          }
        </FormDataConsumer>

        <BooleanInput source="isActive" label="Active?" />
        <TextInput
          source="firstName"
          formClassName={classes.twoColLeft}
          label="First Name"
        />
        <TextInput
          source="lastName"
          formClassName={classes.twoColRight}
          label="Last Name"
        />
        {!isEdit && <TextInput source="email" label="E-mail" />}
        <TextInput source="phoneNumber" label="Phone Number" />
        <RadioButtonGroupInput
          source="gender"
          choices={[
            { id: "male", name: "Male" },
            { id: "female", name: "Female" },
          ]}
        />
        <DateInput source="dateOfBirthTimestamp" parse={dateToTimestamp} />

        <hr />
        <TextInput source="zelleEmail" label="Zelle E-mail" />
        <TextInput source="zellePhoneNumber" label="Zelle Phone Number" />
      </FormTab>
      <FormTab label="Profile">
        <CheckboxGroupInput
          label="Services"
          source="services"
          choices={serviceNameList}
        />
        <CheckboxGroupInput
          label="Enrichment Activities"
          source="enrichmentActivities"
          choices={enrichmentActivitiesList}
        />

        <TextInput
          multiline
          fullWidth
          source="blurbs.childcare"
          label="Blurb (Childcare)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.childcarePlus"
          label="Blurb (Enrichment)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.virtual"
          label="Blurb (Virtual)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.cleaning"
          label="Blurb (Cleaning)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.nightNurse"
          label="Blurb (Night Nurse)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.cooking"
          label="Blurb (Cooking)"
        />
        <TextInput
          multiline
          fullWidth
          source="blurbs.housekeeping"
          label="Blurb (Housekeeping)"
        />

        <TextInput
          multiline
          fullWidth
          source="about.childcare"
          label="About (Childcare)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.childcarePlus"
          label="About (Enrichment)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.virtual"
          label="About (Virtual)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.cleaning"
          label="About (Cleaning)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.nightNurse"
          label="About (Night Nurse)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.cooking"
          label="About (Cooking)"
        />
        <TextInput
          multiline
          fullWidth
          source="about.housekeeping"
          label="About (Housekeeping)"
        />

        <TextInput multiline fullWidth source="funFacts" label="Fun Facts" />

        <DateInput
          source="lastBackgroundCheck"
          label="Last Background Check"
          parse={dateToTimestamp}
        />

        {/* New for becoming-provider */}
        <TextInput
          multiline
          fullWidth
          source="extraService"
          label="Extra Help you can offer families"
        />

        <ArrayInput source="certificates">
          <SimpleFormIterator>
            <TextInput source="name" label="Name" />
            <DateInput
              source="timestamp"
              label="Date"
              parse={dateToTimestamp}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
      <FormTab label="Matching Criteria">
        <BooleanInput source="allowsDogs" label="Dogs OK?" />
        <BooleanInput source="allowsCats" label="Cats OK?" />
        <BooleanInput source="allowsSmoking" label="Smoking OK?" />
        <BooleanInput source="hasCar" label="Has car?" />
        <BooleanInput
          source="hasSpecialNeedsExperience"
          label="Has Special Needs Experience"
        />
        <NumberInput
          source="childcareAgeRange.min"
          label="Childcare Min Age"
          formClassName={classes.twoColLeft}
        />
        <NumberInput
          source="childcareAgeRange.max"
          label="Childcare Max Age"
          formClassName={classes.twoColRight}
        />
        <NumberInput source="reliabilityScore" label="Reliabilty Score" />
        <OperationRadiusInput
          source="operationRadius"
          label="Operation radius"
        />
      </FormTab>
      <FormTab label="Emergency Contacts">
        <ArrayInput source="emergencyContacts">
          <SimpleFormIterator>
            <TextInput source="name" label="Name" />
            <TextInput source="relationship" label="Relationship" />
            <TextInput source="phoneNumber" label="Phone Number" />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
      <FormTab label="Admin Notes">
        <TextInput
          fullWidth
          multiline
          source="adminNotes"
          label="Admin Notes"
        />
      </FormTab>
      <FormTab label="Reviews">
        <ArrayInput source="references" label="References">
          <SimpleFormIterator>
            <TextInput
              fullWidth
              source="name"
              label="Name"
              sx={{ marginTop: "32px" }}
            />
            <Labeled label="Added on" sx={{ marginBottom: "16px" }}>
              <FunctionField
                source="createdTimestamp"
                render={({ createdTimestamp }) =>
                  createdTimestamp
                    ? new Date(createdTimestamp).toDateString()
                    : "-"
                }
              />
            </Labeled>
            <SelectInput
              label="Status"
              source="status"
              choices={referenceStatusList}
            />
            <TextInput fullWidth source="phoneNumber" label="Phone Number" />
            <TextInput fullWidth source="email" label="Email" />
            <TextInput fullWidth source="review" label="Review" multiline />
            <Labeled label="Rating" sx={{ marginBottom: "16px" }}>
              <RatingInput source="rating" />
            </Labeled>
          </SimpleFormIterator>
          <Divider sx={{ marginBottom: "16px", marginTop: "16px" }} />
          <EditRatingsReviews />
          <EditOtherRatingsReviews />
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  );
};

export default UserForm;
